import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import React from "react";

export type ContractTermsData = {
  startDate: number;
  endDate: number;
  currency: "usd" | "eur" | "gbp";
  requiredVideos: {
    count: number;
    duration: number; // in days
  };
  flatFee?: {
    perVideo?: number;
    timePeriod?: {
      amount: number;
      days: number;
    };
  };
  cpmRate?: {
    type: "totalViews" | "perVideo";
    threshold: number;
    rate: number;
    maxPayout?: number;
    cutoffPeriod: number;
  };
};

type DurationOption = "daily" | "weekly" | "monthly" | "contract";
type FlatFeeDurationOption = "monthly" | "flatFeeContract";

// Currency formatting utilities
const getCurrencySymbol = (currency: "usd" | "eur" | "gbp"): string => {
  switch (currency) {
    case "usd":
      return "$";
    case "eur":
      return "€";
    case "gbp":
      return "£";
    default:
      return "$";
  }
};

const formatCurrency = (
  value: number | undefined,
  currency: "usd" | "eur" | "gbp",
): string => {
  if (value === undefined || value === null || isNaN(value)) return "";

  const symbol = getCurrencySymbol(currency);

  // Format based on currency locale conventions
  switch (currency) {
    case "usd":
    case "gbp":
      // US/UK format: $1,234.56 or £1,234.56
      return (
        symbol +
        value.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    case "eur":
      // EU format: €1.234,56
      return (
        symbol +
        value.toLocaleString("de-DE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    default:
      return symbol + value.toFixed(2);
  }
};

const parseCurrencyInput = (input: string): number | undefined => {
  if (!input || input.trim() === "") return undefined;

  // Remove currency symbols and spaces
  let cleaned = input.replace(/[$€£\s]/g, "");

  // If after cleaning there's nothing left, return undefined
  if (!cleaned || cleaned.trim() === "") return undefined;

  // Handle different decimal separators
  // If there's a comma followed by exactly 2 digits at the end, treat it as decimal separator
  if (/,\d{2}$/.test(cleaned)) {
    // EU format: 1.234,56 -> 1234.56
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else {
    // US/UK format: 1,234.56 -> 1234.56
    cleaned = cleaned.replace(/,/g, "");
  }

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? undefined : parsed;
};

const getDaysFromDuration = (
  option: DurationOption,
  startDate: number,
  endDate: number,
): number => {
  switch (option) {
    case "daily":
      return 1;
    case "weekly":
      return 7;
    case "monthly":
      return 30;
    case "contract":
      return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    default:
      return 30;
  }
};

const getDurationFromDays = (
  days: number,
  startDate: number,
  endDate: number,
): DurationOption => {
  const contractDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

  // For contract duration, check first with tolerance to handle rounding issues
  // This should take priority over other matches to ensure "For Entire Contract" works
  if (Math.abs(days - contractDays) <= 1) return "contract";

  // Then check exact matches for standard durations
  if (days === 1) return "daily";
  if (days === 7) return "weekly";
  if (days === 30) return "monthly";

  // Default to closest match
  if (days <= 3) return "daily";
  if (days <= 14) return "weekly";
  if (days <= 45) return "monthly";
  return "contract";
};

const getFlatFeeDurationFromDays = (
  days: number,
  startDate: number,
  endDate: number,
): FlatFeeDurationOption => {
  const contractDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

  if (days === 30) return "monthly";
  if (days === contractDays) return "flatFeeContract";

  // Default to closest match
  if (days <= 45) return "monthly";
  return "flatFeeContract";
};

const getDaysFromFlatFeeDuration = (
  option: FlatFeeDurationOption,
  startDate: number,
  endDate: number,
): number => {
  switch (option) {
    case "monthly":
      return 30;
    case "flatFeeContract":
      return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    default:
      return 30;
  }
};

interface ContractTermsFormProps {
  contractData: ContractTermsData;
  onContractDataChange: (data: ContractTermsData) => void;
  showDateFields?: boolean; // Some forms might want to hide dates
  maxEndDate?: number; // Maximum allowed end date (e.g., campaign end date)
}

export function ContractTermsForm({
  contractData,
  onContractDataChange,
  showDateFields = true,
  maxEndDate,
}: ContractTermsFormProps) {
  // Ensure requiredVideos exists with default values
  const safeRequiredVideos = contractData.requiredVideos || {
    count: 1,
    duration: 30,
  };

  // Track which inputs are currently focused to avoid formatting during typing
  const [focusedField, setFocusedField] = React.useState<string | null>(null);
  const [inputValues, setInputValues] = React.useState<Record<string, string>>(
    {},
  );

  const currentDurationOption = getDurationFromDays(
    safeRequiredVideos.duration,
    contractData.startDate,
    contractData.endDate,
  );

  const currentFlatFeeDurationOption = contractData.flatFee?.timePeriod?.days
    ? getFlatFeeDurationFromDays(
        contractData.flatFee.timePeriod.days,
        contractData.startDate,
        contractData.endDate,
      )
    : "monthly";

  const handleBasicChange = (
    field: keyof ContractTermsData,
    value: string | number | "usd" | "eur" | "gbp",
  ) => {
    onContractDataChange({
      ...contractData,
      [field]: value,
    });
  };

  const handleRequiredVideosChange = (field: "count", value: string) => {
    onContractDataChange({
      ...contractData,
      requiredVideos: {
        ...safeRequiredVideos,
        [field]: parseInt(value) || 0,
      },
    });
  };

  const handleDurationChange = (durationOption: DurationOption) => {
    const days = getDaysFromDuration(
      durationOption,
      contractData.startDate,
      contractData.endDate,
    );
    onContractDataChange({
      ...contractData,
      requiredVideos: {
        ...safeRequiredVideos,
        duration: days,
      },
    });
  };

  const handleCurrencyInputChange = (fieldName: string, value: string) => {
    // Store the raw input value while user is typing
    setInputValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleCurrencyInputBlur = (fieldName: string, value: string) => {
    // Clear the raw input value and update the contract data
    setInputValues((prev) => {
      const { [fieldName]: removed, ...rest } = prev;
      return rest;
    });
    setFocusedField(null);

    if (fieldName === "flatFeePerVideo") {
      handleFlatFeeChange("perVideo", value);
    } else if (fieldName === "flatFeeAmount") {
      handleFlatFeeChange("timePeriodAmount", value);
    } else if (fieldName === "cpmRate") {
      handleCpmRateChange("rate", value);
    } else if (fieldName === "cpmMaxPayout") {
      handleCpmRateChange("maxPayout", value);
    }
  };

  const handleFlatFeeChange = (
    field: "perVideo" | "timePeriodAmount" | "timePeriodDays",
    value: string,
  ) => {
    const currentFlatFee = contractData.flatFee || {};

    if (field === "perVideo") {
      const parsedValue = parseCurrencyInput(value);
      onContractDataChange({
        ...contractData,
        flatFee:
          parsedValue === undefined && !value.trim()
            ? // If input is completely empty, remove the perVideo field entirely
              { ...currentFlatFee, perVideo: undefined }
            : // Otherwise set the parsed value
              { ...currentFlatFee, perVideo: parsedValue },
      });
    } else if (field === "timePeriodAmount") {
      const parsedValue = parseCurrencyInput(value);
      onContractDataChange({
        ...contractData,
        flatFee: {
          ...currentFlatFee,
          timePeriod: {
            amount: parsedValue ?? 0, // amount is required, default to 0
            days: currentFlatFee.timePeriod?.days || 30,
          },
        },
      });
    } else if (field === "timePeriodDays") {
      onContractDataChange({
        ...contractData,
        flatFee: {
          ...currentFlatFee,
          timePeriod: {
            amount: currentFlatFee.timePeriod?.amount || 0,
            days: value ? parseInt(value) : 30,
          },
        },
      });
    }
  };

  const handleFlatFeeDurationChange = (
    durationOption: FlatFeeDurationOption,
  ) => {
    const days = getDaysFromFlatFeeDuration(
      durationOption,
      contractData.startDate,
      contractData.endDate,
    );
    const currentFlatFee = contractData.flatFee || {};

    onContractDataChange({
      ...contractData,
      flatFee: {
        ...currentFlatFee,
        timePeriod: {
          amount: currentFlatFee.timePeriod?.amount || 0,
          days: days,
        },
      },
    });
  };

  const handleCpmRateChange = (
    field: "type" | "threshold" | "rate" | "maxPayout" | "cutoffPeriod",
    value: string | "totalViews" | "perVideo",
  ) => {
    const currentCpmRate = contractData.cpmRate || {
      type: "perVideo",
      threshold: 0,
      rate: 0,
      cutoffPeriod: 10, // Default to 10 days
    };

    if (field === "type") {
      onContractDataChange({
        ...contractData,
        cpmRate: {
          ...currentCpmRate,
          type: value as "totalViews" | "perVideo",
        },
      });
    } else if (field === "threshold") {
      onContractDataChange({
        ...contractData,
        cpmRate: {
          ...currentCpmRate,
          threshold: typeof value === "string" ? parseInt(value) || 0 : 0,
        },
      });
    } else if (field === "rate") {
      const parsedValue =
        typeof value === "string" ? parseCurrencyInput(value) : undefined;
      onContractDataChange({
        ...contractData,
        cpmRate: {
          ...currentCpmRate,
          rate: parsedValue ?? 0, // rate is required, default to 0
        },
      });
    } else if (field === "maxPayout") {
      const parsedValue =
        typeof value === "string" && value
          ? parseCurrencyInput(value)
          : undefined;
      onContractDataChange({
        ...contractData,
        cpmRate: {
          ...currentCpmRate,
          maxPayout:
            typeof value === "string" && !value.trim()
              ? undefined
              : parsedValue, // maxPayout is optional
        },
      });
    } else if (field === "cutoffPeriod") {
      onContractDataChange({
        ...contractData,
        cpmRate: {
          ...currentCpmRate,
          cutoffPeriod: typeof value === "string" ? parseInt(value) || 10 : 10, // cutoffPeriod is required, default to 10
        },
      });
    }
  };

  // Get the display value for currency inputs
  const getCurrencyInputValue = (
    fieldName: string,
    actualValue: number | undefined,
  ): string => {
    // If this field is currently focused, show the raw input value
    if (focusedField === fieldName && inputValues[fieldName] !== undefined) {
      return inputValues[fieldName];
    }
    // Otherwise show the formatted value
    return formatCurrency(actualValue, contractData.currency);
  };

  return (
    <div className="space-y-6">
      {/* Basic Contract Terms */}
      {showDateFields && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              id="startDate"
              type="date"
              value={
                new Date(contractData.startDate).toISOString().split("T")[0]
              }
              onChange={(e) =>
                handleBasicChange(
                  "startDate",
                  new Date(e.target.value).getTime(),
                )
              }
            />
          </div>
          <div>
            <Label htmlFor="endDate">End Date</Label>
            <Input
              id="endDate"
              type="date"
              value={new Date(contractData.endDate).toISOString().split("T")[0]}
              max={
                maxEndDate
                  ? new Date(maxEndDate).toISOString().split("T")[0]
                  : undefined
              }
              onChange={(e) =>
                handleBasicChange("endDate", new Date(e.target.value).getTime())
              }
            />
            {maxEndDate && contractData.endDate > maxEndDate && (
              <p className="text-xs text-red-500 mt-1">
                End date cannot be after campaign end date (
                {new Date(maxEndDate).toLocaleDateString()})
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="currency">Currency</Label>
            <Select
              value={contractData.currency}
              onValueChange={(value: "usd" | "eur" | "gbp") =>
                handleBasicChange("currency", value)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="usd">USD ($)</SelectItem>
                {/* <SelectItem value="eur">EUR (€)</SelectItem>
                <SelectItem value="gbp">GBP (£)</SelectItem> */}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {!showDateFields && (
        <div>
          <Label htmlFor="currency">Currency</Label>
          <Select
            value={contractData.currency}
            onValueChange={(value: "usd" | "eur" | "gbp") =>
              handleBasicChange("currency", value)
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="usd">USD ($)</SelectItem>
              <SelectItem value="eur">EUR (€)</SelectItem>
              <SelectItem value="gbp">GBP (£)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Required Videos Section */}
      <div className="space-y-4 p-4 border rounded-lg">
        <h4 className="font-medium">Required Videos</h4>
        <p className="text-sm text-gray-600">
          This is the total number of videos that need to be published. If you
          want content posted across multiple platforms, multiply your desired
          video count by the number of platforms (e.g., 7 videos × 3 platforms =
          21 total videos).
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="videoCount">Video Count</Label>
            <Input
              id="videoCount"
              type="string"
              min="1"
              value={safeRequiredVideos.count}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || /^\d+$/.test(value)) {
                  handleRequiredVideosChange("count", value);
                }
              }}
              placeholder="e.g., 10"
            />
          </div>
          <div>
            <Label htmlFor="videoDuration">Frequency</Label>
            <Select
              value={currentDurationOption}
              onValueChange={handleDurationChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="contract">For Entire Contract</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Flat Fee Section */}
      <div className="space-y-4 p-4 border rounded-lg">
        <div className="flex justify-between items-center">
          <h4 className="font-medium">Flat Fee Options</h4>
          <h6 className="text-sm text-gray-500">(Optional)</h6>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <Label htmlFor="flatFeePerVideo">Flat Fee per Video</Label>
            <Input
              id="flatFeePerVideo"
              type="text"
              value={getCurrencyInputValue(
                "flatFeePerVideo",
                contractData.flatFee?.perVideo,
              )}
              onChange={(e) =>
                handleCurrencyInputChange("flatFeePerVideo", e.target.value)
              }
              onFocus={() => setFocusedField("flatFeePerVideo")}
              onBlur={(e) =>
                handleCurrencyInputBlur("flatFeePerVideo", e.target.value)
              }
              placeholder={`e.g., ${getCurrencySymbol(contractData.currency)}100.00`}
            />
          </div>

          <div className="flex items-center justify-center">
            <span className="text-sm font-medium text-gray-500">AND/OR</span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="flatFeeAmount">Flat Fee</Label>
              <Input
                id="flatFeeAmount"
                type="text"
                value={getCurrencyInputValue(
                  "flatFeeAmount",
                  contractData.flatFee?.timePeriod?.amount,
                )}
                onChange={(e) =>
                  handleCurrencyInputChange("flatFeeAmount", e.target.value)
                }
                onFocus={() => setFocusedField("flatFeeAmount")}
                onBlur={(e) =>
                  handleCurrencyInputBlur("flatFeeAmount", e.target.value)
                }
                placeholder={`e.g., ${getCurrencySymbol(contractData.currency)}1,000.00`}
              />
            </div>
            <div>
              <Label htmlFor="flatFeeDays">Frequency</Label>
              <Select
                value={currentFlatFeeDurationOption}
                onValueChange={handleFlatFeeDurationChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="flatFeeContract">
                    For Entire Contract
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* CPM Section */}
      <div className="space-y-4 p-4 border rounded-lg">
        <div className="flex justify-between items-center">
          <h4 className="font-medium">CPM (Cost Per Thousand Views)</h4>
          <h6 className="text-sm text-gray-500">(Optional)</h6>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <Label htmlFor="cpmType">Type</Label>
            <Select
              value={contractData.cpmRate?.type || "perVideo"}
              onValueChange={(value: "totalViews" | "perVideo") =>
                handleCpmRateChange("type", value)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="perVideo">Per Video</SelectItem>
                <SelectItem value="totalViews">Total Views</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              {contractData.cpmRate?.type === "totalViews"
                ? "Payment based on total views across all videos"
                : "Payment calculated separately for each video"}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cpmThreshold">Minimum Views Required</Label>
              <Input
                id="cpmThreshold"
                type="string"
                min="0"
                value={contractData.cpmRate?.threshold ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d+$/.test(value)) {
                    handleCpmRateChange("threshold", value);
                  }
                }}
                placeholder="e.g., 10000"
              />
            </div>
            <div>
              <Label htmlFor="cpmRate">CPM Rate</Label>
              <Input
                id="cpmRate"
                type="text"
                value={getCurrencyInputValue(
                  "cpmRate",
                  contractData.cpmRate?.rate,
                )}
                onChange={(e) =>
                  handleCurrencyInputChange("cpmRate", e.target.value)
                }
                onFocus={() => setFocusedField("cpmRate")}
                onBlur={(e) =>
                  handleCurrencyInputBlur("cpmRate", e.target.value)
                }
                placeholder={`e.g., ${getCurrencySymbol(contractData.currency)}5.00`}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cpmMaxPayout">Maximum Payout (Optional)</Label>
              <Input
                id="cpmMaxPayout"
                type="text"
                value={getCurrencyInputValue(
                  "cpmMaxPayout",
                  contractData.cpmRate?.maxPayout,
                )}
                onChange={(e) =>
                  handleCurrencyInputChange("cpmMaxPayout", e.target.value)
                }
                onFocus={() => setFocusedField("cpmMaxPayout")}
                onBlur={(e) =>
                  handleCurrencyInputBlur("cpmMaxPayout", e.target.value)
                }
                placeholder={`e.g., ${getCurrencySymbol(contractData.currency)}500.00`}
              />
            </div>
            <div>
              <Label htmlFor="cutoffPeriod">Cutoff Period (Days)</Label>
              <Input
                id="cutoffPeriod"
                type="string"
                min="1"
                value={contractData.cpmRate?.cutoffPeriod ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d+$/.test(value)) {
                    handleCpmRateChange("cutoffPeriod", value);
                  }
                }}
                placeholder="e.g., 30"
              />
              <p className="text-xs text-gray-500 mt-1">
                {contractData.cpmRate?.type === "totalViews"
                  ? "Days after contract ends to calculate total views"
                  : "Days after video is posted to calculate earnings"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
